import React, { useState, useEffect, useRef } from 'react';
import { Target, Zap, Clock, Database } from 'lucide-react';
import { performanceStats } from '../mockData';

const iconMap = {
  Target: Target,
  Zap: Zap,
  Clock: Clock,
  Database: Database
};

const colorMap = {
  green: {
    bg: 'from-emerald-500/10 to-emerald-600/10',
    border: 'border-emerald-500/30',
    icon: 'text-emerald-400',
    glow: 'shadow-emerald-500/20',
    gradient: 'from-emerald-400 to-cyan-400'
  },
  cyan: {
    bg: 'from-cyan-500/10 to-cyan-600/10',
    border: 'border-cyan-500/30',
    icon: 'text-cyan-400',
    glow: 'shadow-cyan-500/20',
    gradient: 'from-cyan-400 to-blue-400'
  },
  magenta: {
    bg: 'from-fuchsia-500/10 to-fuchsia-600/10',
    border: 'border-fuchsia-500/30',
    icon: 'text-fuchsia-400',
    glow: 'shadow-fuchsia-500/20',
    gradient: 'from-fuchsia-400 to-pink-400'
  }
};

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 2000, isVisible }) => {
  const [count, setCount] = useState(value); // Start with actual value instead of '0'
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!isVisible || hasAnimated) return;

    setHasAnimated(true);
    
    // Extract number from value string (e.g., "94.7%" -> 94.7)
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
    const suffix = value.replace(/[0-9.]/g, '');
    
    if (isNaN(numericValue)) {
      setCount(value);
      return;
    }

    // Start from 0 and animate to actual value
    const increment = numericValue / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= numericValue) {
        setCount(value);
        clearInterval(timer);
      } else {
        // Format based on if it has decimal
        const formatted = numericValue % 1 === 0 
          ? Math.floor(current) 
          : current.toFixed(1);
        setCount(formatted + suffix);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isVisible, value, duration, hasAnimated]);

  return <span>{count}</span>;
};

// Intersection Observer Hook
const useIntersectionObserver = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, { threshold: 0.3, ...options });

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, options]);

  return isVisible;
};

const PerformanceStats = () => {
  const sectionRef = useRef(null);
  const isVisible = useIntersectionObserver(sectionRef);

  return (
    <section id="performance" className="relative bg-black py-24 overflow-hidden" ref={sectionRef}>
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0, 255, 65, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 65, 0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Glow Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-6">
            <Zap className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-semibold tracking-wide">PROVEN PERFORMANCE</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
            <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              AI Performance
            </span>
            {' '}That Speaks
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Real numbers. Real results. Built for institutional-grade reliability and precision.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {performanceStats.map((stat, index) => {
            const IconComponent = iconMap[stat.icon];
            const colors = colorMap[stat.color];
            return (
              <div
                key={index}
                className={`group relative bg-gradient-to-br from-slate-900/50 to-slate-950/50 backdrop-blur-sm border ${colors.border} rounded-2xl p-8 hover:border-opacity-100 transition-all duration-500 text-center hover:scale-105 hover:shadow-2xl ${colors.glow}`}
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  animation: isVisible ? 'fadeInUp 0.6s ease-out forwards' : 'none',
                  opacity: isVisible ? 1 : 0
                }}
              >
                {/* Glow Effect on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`}></div>
                
                {/* Icon */}
                <div className={`relative w-20 h-20 bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                  <IconComponent className={`w-10 h-10 ${colors.icon} drop-shadow-[0_0_10px_currentColor]`} />
                </div>
                
                {/* Animated Counter */}
                <div className={`relative text-5xl lg:text-6xl font-black bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent mb-3 tracking-tight`}>
                  <AnimatedCounter value={stat.value} isVisible={isVisible} />
                </div>
                
                {/* Label */}
                <div className="relative text-xl font-bold text-white mb-2">{stat.label}</div>
                
                {/* Subtitle */}
                <div className="relative text-xs text-slate-500 font-semibold tracking-widest uppercase">{stat.subtitle}</div>

                {/* Decorative Line */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${colors.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-2xl`}></div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-slate-400 mb-6 text-lg">
            Ready to experience these results yourself?
          </p>
          <a
            href="#profit-simulator"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('profit-simulator')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-bold rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all duration-300"
          >
            <Zap className="w-5 h-5" />
            <span>Calculate Your Potential Profit</span>
          </a>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default PerformanceStats;
